<template>
  <Card class="exclusivity-card" :class="{'exclusivity-card--wide': wide}" v-if="!data || exclusivity" v-bind="attributes">
    <div ref="exclusivityCard" class="exclusivity-card__content" v-if="exclusivity" :data-id="exclusivity.id">
      <div class="exclusivity-card__header">
        <img 
          v-if="exclusivity.content?.logotype?.['fr-FR']" 
          :src="exclusivity.content.logotype['fr-FR']" 
          :alt="exclusivity.content?.company?.['fr-FR']"
          class="exclusivity-card__logo"
        />
        <mks-heading 
          tag="h5" 
          class="exclusivity-card__title"
          v-html="exclusivity.content?.title?.['fr-FR']"
        />
      </div>

      <div class="exclusivity-card__bullets">
        <ul v-if="exclusivity.content?.bullets?.['fr-FR']">
          <li 
            v-for="(bullet, index) in exclusivity.content.bullets['fr-FR']" 
            :key="index"
            v-html="bullet"
          />
        </ul>
      </div>

      <mks-link v-if="exclusivity.actionType === 'link'" :to="exclusivity.url" type="menu" target="_blank" @click.prevent="trackClickAndNavigate">
        <mks-button 
          type="primary"
          tag="button"
          size="small"
          icon="arrow-right"
          animateIcon
          class="exclusivity-card__cta"
        >
          <template v-if="exclusivity.content?.button?.['fr-FR']">{{ exclusivity.content.button['fr-FR'] }}</template>
          <template v-else>{{ _x('En savoir plus', 'Texte du bouton') }}</template>
        </mks-button>
      </mks-link>

      <mks-button 
          v-else-if="exclusivity.actionType === 'form'"
          type="primary"
          tag="button"
          size="small"
          icon="arrow-right"
          animateIcon
          class="exclusivity-card__cta"
          @click="openModalExclusivity"
        >
          <template v-if="exclusivity.content?.button?.['fr-FR']">{{ exclusivity.content.button['fr-FR'] }}</template>
          <template v-else>{{ _x('En savoir plus', 'Texte du bouton') }}</template>
        </mks-button>
    </div>
  </Card>
  <slot v-else v-bind="attributes"><!-- no exclusivity, replace with children --></slot>
</template>

<script setup>
import striptags from "striptags";
import { useTemplateRef } from 'vue'
import { useStore } from 'vuex'
import { whenever } from '@vueuse/core'
import Card from "../../molecules/Cards/Card.vue";

const NuxtApp = useNuxtApp();
const {$article, $event} = NuxtApp;

const props = defineProps({
  exclusivityId: {
    type: String,
    required: false,
    default: null
  },
  matchingData: {
    type: Object,
    required: false,
    default: () => ({})
  },
  wide: {
    type: Boolean,
    required: false,
    default: false
  },
  context: {
    type: String,
    required: false,
    default: 'chiche-homepage',
    enum: [
      'chiche-homepage',
      'chiche-event-page',
      'chiche-event-list',
      'chiche-event-detail'
    ]
  }
})

const store = useStore();
const profile = computed(() => store.getters['user/profile']);

const waitMatchingData = computed(() => {
  return ['chiche-article-sidebar', 'chiche-event-register'].includes(props.context)
})


const matchingData = computed(() => {

  if (props.context === 'chiche-article-sidebar' && $article?.value) {
    return {
      "@context": "https://schema.org",
      '@type': 'Article',
      name: $article.value.data.title,
      keywords: $article.value.data.tags,
      articleSection: $article.value.data.pilier?.value?.data?.title,
      about: $article.value.data.thematique?.value?.data?.name,
      abstract: $article.value.data.descriptionCourte
    };
  } else if (props.context === 'chiche-event-register' && $event?.value?.title) {
    return {
      "@context": "https://schema.org",
      '@type': 'Event',
      about:  $event.value.topic?.name ? $event.value.topic.name[$event.value.lang] : '',
      additionalType: $event.value.type?.name ? $event.value.type.name[$event.value.lang] : undefined,
      description: striptags(
        $event.value.description?.[$event.value.lang] ?
          $event.value.description[$event.value.lang].substring(0, 250) : ''
      ),
      location: {
        address: $event.value.visio ? undefined : $event.value.address ? $event.value.address[$event.value.lang].formattedAddress : undefined,
      },
      name: $event.value.title[$event.value.lang]
    };
  }
  return {}
})

const data = ref({})

watch(() => matchingData.value, async (matchingDataValue) => {
  if (waitMatchingData.value && !Object.keys(matchingDataValue).length) {
    return
  }

  const params = {
    region: 'eu',
    context: props.context,
    matchingData: {...matchingDataValue, ...props.matchingData}
  }
  if (props.exclusivityId) {
    params.exclusivity = props.exclusivityId
  }
  const { data: exclusivityData } = await NuxtApp.$jobs.Exclusivity.match(params)
  data.value = exclusivityData

}, { deep: true, immediate: true })

const exclusivity = computed(() => {
  return data.value?.success ? data.value.exclusivities[0] : null
})

const url = useRoute().path

const tracking = computed(() => {
  const params = {
    id: exclusivity.value.id,
    category: "",
    context: {
      context: props.context,
      url
    },
    referrer: "https://chiche.makesense.org/"
  }
  if (profile.value?._id) {
    params.uid = profile.value._id
  }
  return params
})

const track = (type = "view") => {
  NuxtApp.$jobs.Exclusivity.track({
    ...tracking.value,
    category: type
  })
}

const exclusivityCard = useTemplateRef('exclusivityCard')
const exclusivityCardIsVisible = useElementVisibility(exclusivityCard)

whenever(exclusivityCardIsVisible, () => {
    track("view")
}, {
  once: true
})

const emit = defineEmits(['exclusivity:open'])

const trackClickAndNavigate = (e) => {
  track("click")
  window.open(e.currentTarget.href, '_blank')
}

const openModalExclusivity = () => {
  track("click")
  emit('exclusivity:open')
  NuxtApp.$store.dispatch('modals/open', {
    name: 'exclusivity',
    props: { exclusivity: exclusivity.value, context: props.context }
  })
}

</script>


<style lang="scss" scoped>
.exclusivity-card {
  background-color: #F7F6F3;

  &__content {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    text-align: center;
    padding: 0.625rem 1.25rem 1.375rem;
    max-width: 42rem;
    margin: 0 auto;
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.75rem;
    text-wrap: balance;
  }

  &__logo {
    width: 100%;
    height: auto;
    max-width: 12.5rem;
  }

  &__title {
    margin: 0;
  }

  &__bullets {
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 1rem;

      font-weight: 500;
      
    }
    li {
        text-wrap: balance;
        // text-indent: -1.25ch;
    }
  }

  &__cta {
    width: auto;
  }

  @container (min-width: 31.25rem) {
    &__content {
      padding: 0 3rem;
    }
  }

  &--wide {
    .exclusivity-card {
      &__content {
        max-width: 100%;
      }
    }
  }
}
</style>
